import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Injector,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { faWindowMinimize } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@sama/app-state';
import { DirectiveSuperclass } from '@sama/ng-core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { AbstractAnnotationStore } from '../../../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { OutputInfo } from '../../../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../../h3ml/state/task-workspace-store';

@Component({
  selector: 'h3-bulk-remove-keyframes-modal',
  templateUrl: './bulk-remove-keyframes-modal.component.html',
  styleUrls: ['./bulk-remove-keyframes-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BulkRemoveKeyframesModalComponent
  extends DirectiveSuperclass
  implements OnInit
{
  lastFrameNumber = 0;
  startFrameControl!: UntypedFormControl;
  endFrameControl!: UntypedFormControl;
  includeKeyframesFormControl!: UntypedFormControl;
  includeDynamicTagsFormControl!: UntypedFormControl;
  keyframeToPreserveOnDeletionFormControl!: UntypedFormControl;
  disableTooltipText$!: Observable<string>;
  faWindowMinimize = faWindowMinimize;

  private form!: UntypedFormGroup;
  private store!: AbstractAnnotationStore;

  constructor(
    private dialogRef: MatDialogRef<BulkRemoveKeyframesModalComponent>,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA)
    private dialogData: {
      store: AbstractAnnotationStore;
      taskWorkspaceStore: TaskWorkspaceStore;
      outputInfoStore: Store<OutputInfo>;
    },
    injector: Injector,
  ) {
    super(injector);
    this.store = dialogData.store;
  }

  ngOnInit() {
    this.lastFrameNumber = this.dialogData.taskWorkspaceStore.getMaxFrameNumber(
      this.dialogData.outputInfoStore.state().output.name,
    );
    this.initForm();
    this.setUpStartFrameValidations();
    this.setUpEndFrameValidations();

    this.disableTooltipText$ = this.form.valueChanges.pipe(
      startWith(null),
      map(() => this.getDisableTooltipText()),
    );
  }

  close() {
    this.store('bulkRemovePreferences').set(this.form.value.inclusions);
    this.dialogRef.close({
      startFrameNumber: this.startFrameControl.value,
      endFrameNumber: this.endFrameControl.value,
    });
  }

  cancel() {
    this.dialogRef.close();
  }

  private getDisableTooltipText() {
    if (this.form.get('frameRange')!.invalid) {
      return 'Enter a valid frame range';
    }

    if (this.form.get('inclusions')!.invalid) {
      return 'Keyframes or dynamic attributes must be selected';
    }

    return '';
  }

  private initForm() {
    this.initFormControls();

    this.form = this.formBuilder.group({
      frameRange: this.formBuilder.group(
        {
          startFrameNumber: this.startFrameControl,
          endFrameNumber: this.endFrameControl,
        },
        { validators: this.validateFrameRange.bind(this) },
      ),
      inclusions: this.formBuilder.group(
        {
          includeKeyFrames: this.includeKeyframesFormControl,
          includeDynamicTags: this.includeDynamicTagsFormControl,
          keyframeToPreserveOnDeletion:
            this.keyframeToPreserveOnDeletionFormControl,
        },
        { validators: this.validateInclusions },
      ),
    });
  }

  private setUpEndFrameValidations() {
    this.subscribeTo(
      this.endFrameControl.valueChanges.pipe(distinctUntilChanged()),
      (endFrameNumber) => {
        const correctedValue = Math.min(
          Math.floor(endFrameNumber),
          this.lastFrameNumber,
        );

        this.endFrameControl.setValue(correctedValue);
      },
    );
  }

  private setUpStartFrameValidations() {
    this.subscribeTo(
      this.startFrameControl.valueChanges.pipe(distinctUntilChanged()),
      (startFrameNumber) => {
        const correctedValue = Math.min(
          Math.floor(startFrameNumber),
          this.lastFrameNumber,
        );

        this.startFrameControl.setValue(correctedValue);
      },
    );
  }

  private initFormControls() {
    this.startFrameControl = new UntypedFormControl(0, [
      Validators.required,
      Validators.min(0),
    ]);

    this.endFrameControl = new UntypedFormControl(this.lastFrameNumber, [
      Validators.required,
      Validators.max(this.lastFrameNumber),
    ]);

    this.includeKeyframesFormControl = new UntypedFormControl(
      this.store.state().bulkRemovePreferences.includeKeyFrames,
    );
    this.includeDynamicTagsFormControl = new UntypedFormControl(
      this.store.state().bulkRemovePreferences.includeDynamicTags,
    );
    this.keyframeToPreserveOnDeletionFormControl = new UntypedFormControl(
      this.store.state().bulkRemovePreferences.keyframeToPreserveOnDeletion,
    );
  }

  private validateInclusions(inclusions: AbstractControl) {
    const hasAtLeastOneInclusion =
      inclusions.value.includeKeyFrames || inclusions.value.includeDynamicTags;
    return hasAtLeastOneInclusion ? null : { inclusionRequired: true };
  }

  private validateFrameRange() {
    if (this.endFrameControl.value < this.startFrameControl.value) {
      return { range: true };
    } else {
      return null;
    }
  }
}
